import { useEffect, useRef } from "react";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(",");

function isolateBackground(panel) {
  if (!panel) return () => {};

  const changed = [];
  let branch = panel;
  while (branch?.parentElement) {
    const parent = branch.parentElement;
    for (const sibling of parent.children) {
      if (sibling === branch
          || sibling.tagName === "SCRIPT"
          || sibling.tagName === "STYLE"
          || sibling.hasAttribute("data-overlay-backdrop")) continue;
      changed.push({
        element: sibling,
        hadInert: sibling.hasAttribute("inert"),
        inert: Boolean(sibling.inert),
        hadAriaHidden: sibling.hasAttribute("aria-hidden"),
        ariaHidden: sibling.getAttribute("aria-hidden"),
      });
      sibling.setAttribute("inert", "");
      sibling.inert = true;
      sibling.setAttribute("aria-hidden", "true");
    }
    if (parent === document.body) break;
    branch = parent;
  }

  return () => {
    for (const state of changed.reverse()) {
      if (state.hadInert) state.element.setAttribute("inert", "");
      else state.element.removeAttribute("inert");
      state.element.inert = state.inert;
      if (state.hadAriaHidden) state.element.setAttribute("aria-hidden", state.ariaHidden);
      else state.element.removeAttribute("aria-hidden");
    }
  };
}

/** Shared modal focus, Escape, focus-return, scroll-lock and background isolation. */
export default function useAccessibleOverlay(open, onClose) {
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const closeRef = useRef(onClose);

  useEffect(() => { closeRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!open) return undefined;

    const previousFocus = document.activeElement;
    const returnTarget = triggerRef.current || previousFocus;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    let restoreBackground = () => {};

    const frame = window.requestAnimationFrame(() => {
      const panel = panelRef.current;
      const preferred = panel?.querySelector("[data-autofocus]");
      const first = preferred || panel?.querySelector(FOCUSABLE) || panel;
      first?.focus({ preventScroll: true });
      restoreBackground = isolateBackground(panel);
    });

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeRef.current?.();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;

      const focusable = [...panelRef.current.querySelectorAll(FOCUSABLE)]
        .filter((element) => !element.hasAttribute("hidden")
          && !element.closest("[inert]")
          && !element.closest('[aria-hidden="true"]'));
      if (!focusable.length) {
        event.preventDefault();
        panelRef.current.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", handleKeyDown);
      restoreBackground();
      document.body.style.overflow = previousOverflow;
      if (returnTarget && document.contains(returnTarget)) {
        window.requestAnimationFrame(() => returnTarget.focus({ preventScroll: true }));
      }
    };
  }, [open]);

  return { panelRef, triggerRef };
}
